import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { handleOnSettled } from '@utils/formHelpers';
import { DocumentResp, useCreateDocument } from '@services/documents/create';
import { useUpdateDocument } from '@services/documents/update';
import { FormProps } from '@forms/types';
import { FileFormProps, FileFormValues } from './types';
import { useErrors } from '@hooks/errors';
import { defaultValues, formSchema } from './constants';
import { parseFileErrors } from './helpers';

export const useFileForm = ({
  id,
  jobId,
  files,
  queryType,
  successHandler,
}: FormProps<FileFormProps, DocumentResp>) => {
  const handleErrors = useErrors();

  const form = useForm<FileFormValues>({
    resolver: zodResolver(formSchema(queryType)),
    defaultValues,
  });

  const { setError } = form;

  const callback = (resp: DocumentResp) => {
    handleOnSettled<DocumentResp>(resp, successHandler, () =>
      handleErrors(parseFileErrors(resp), (key, message) =>
        setError(key as keyof FileFormValues, { message }),
      ),
    );
  };

  const create = useCreateDocument({ callback, queryType });
  const update = useUpdateDocument({ callback, queryType });

  const onSubmit = async ({ name, subtaskId }: FileFormValues) => {
    if (!jobId) return;
    const formData = new FormData();

    if (id) {
      if (files) formData.append('document[file]', files[0]);
      formData.append('document[name]', name || 'file');
    } else if (files) {
      formData.append('document[job_id]', jobId);
      if (subtaskId === 'other') {
        Array.from(files).forEach((file) => {
          formData.append(`document[files][][file]`, file);
          formData.append(`document[files][][name]`, name || '');
        });
      } else {
        formData.append('document[subtask_id]', subtaskId || '');
        Array.from(files).forEach((file) => {
          formData.append(`document[files][][file]`, file);
        });
      }
    }
    if (id) update.mutate({ id, body: formData });
    else create.mutate(formData);
  };

  return {
    form,
    onSubmit,
  };
};
